<template>
  <div class="common-layout">
    <el-container>
      <el-header class="header-nav">
        <div class="nav-items">
          <!-- Logo -->
          <div class="logo" @click="selectTab('dashboard')">
            <i class="crochet-icon">_hooks</i>
            <span class="logo-text">钩织乐园</span>
          </div>
          
          <!-- 帖子广场 -->
          <div 
            class="nav-item" 
            :class="{ active: activeTab === 'square' }" 
            @click="goToSquare"
          >
            <el-icon class="nav-icon"><ChatDotRound /></el-icon>
            <span>帖子广场</span>
          </div>
          
          <!-- 显示二级菜单的容器 -->
          <transition name="slide-from-left">
            <div 
              v-show="showSubmenu" 
              class="sidebar-submenu"
              @click.stop
            >
              <div class="submenu-header">
                <span class="submenu-title">功能菜单</span>
                <el-button 
                  class="close-btn" 
                  :link="true"
                  size="small"
                  @click="showSubmenu = false"
                >
                  <el-icon><Close /></el-icon>
                </el-button>
              </div>
              <ul class="submenu-list">
                <li class="submenu-item" @click="selectTab('dashboard')">
                  <el-icon class="submenu-icon"><House /></el-icon>
                  <span>首页</span>
                </li>
                <li class="submenu-item" @click="selectTab('square')">
                  <el-icon class="submenu-icon"><ChatDotRound /></el-icon>
                  <span>帖子广场</span>
                </li>
                <li class="submenu-item" @click="goToUserCollection">
                  <el-icon class="submenu-icon"><Star /></el-icon>
                  <span>我的收藏</span>
                </li>
                <li class="submenu-item" @click="goToCreatePost">
                  <el-icon class="submenu-icon"><EditPen /></el-icon>
                  <span>发布帖子</span>
                </li>
                <li class="submenu-item" @click="goToPersonalPosts">
                  <el-icon class="submenu-icon"><Document /></el-icon>
                  <span>我的帖子</span>
                </li>
                <li class="submenu-item" @click="goToChat">
                  <el-icon class="submenu-icon"><ChatLineRound /></el-icon>
                  <span>私信聊天</span>
                  <el-badge v-if="unreadChatMessages > 0" :value="unreadChatMessages" class="submenu-icon" type="danger" />
                </li>
                <li class="submenu-item" @click="selectTab('ai')">
                  <el-icon class="submenu-icon"><MagicStick /></el-icon>
                  <span>AI问答</span>
                </li>
                <li class="submenu-item" @click="selectTab('profile')">
                  <el-icon class="submenu-icon"><User /></el-icon>
                  <span>个人中心</span>
                </li>
                <li class="submenu-item" @click="goToAdminLogin">
                  <el-icon class="submenu-icon"><SetUp /></el-icon>
                  <span>管理后台</span>
                </li>
              </ul>
            </div>
          </transition>
          
          <!-- AI问答 -->
          <div class="nav-item" :class="{ active: activeTab === 'ai' }" @click="selectTab('ai')">
            <el-icon class="nav-icon"><MagicStick /></el-icon>
            <span>AI问答</span>
          </div>
          
          <!-- 个人中心 -->
          <div class="nav-item" :class="{ active: activeTab === 'profile' }" @click="selectTab('profile')">
            <el-icon class="nav-icon"><User /></el-icon>
            <span>个人中心</span>
          </div>

          <!-- 管理后台登录 -->
          <div class="nav-item" @click="goToAdminLogin">
            <el-icon class="nav-icon"><SetUp /></el-icon>
            <span>管理后台</span>
          </div>

          <!-- 返回按钮，当在帖子详情页面时显示 -->
          <div v-if="activeTab === 'postDetail'" class="nav-item back-btn" @click="goBackToSquare">
            <el-icon class="nav-icon"><ArrowLeft /></el-icon>
            <span>返回</span>
          </div>
          
          <!-- 用户头像与用户名 -->
          <div class="user-info">
            <el-avatar 
              :size="40" 
              :src="getAvatarSrc(computedUserInfo?.avatar)" 
              class="user-avatar"
              :class="{ 'no-click': !computedIsLoggedIn }"
              @click="handleAvatarClick"
            ></el-avatar>
            
            <el-dropdown v-if="computedIsLoggedIn" @command="handleCommand">
              <span class="username dropdown-link">
                {{ computedUserInfo?.name || computedUserInfo?.username }}
                <el-icon class="el-icon--right"><arrow-down /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="announcements">
                    <el-icon><ChatDotSquare /></el-icon>
                    系统公告 ({{ unreadAnnouncements }})
                  </el-dropdown-item>
                  <el-dropdown-item command="notifications">
                    <el-icon><Bell /></el-icon>
                    互动消息 ({{ unreadNotifications }})
                  </el-dropdown-item>
                  <el-dropdown-item command="logout" divided>
                    <el-icon><SwitchButton /></el-icon>
                    退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            
            <span v-else class="username login-prompt" @click="goToLogin">请登录</span>
          </div>
        </div>
      </el-header>
      
      <!-- 头像上传弹窗 -->
      <AvatarUploadModal 
        v-model="showAvatarUploadModal" 
        :currentAvatar="computedUserInfo?.avatar"
        @uploaded="handleAvatarUploaded"
        @close="handleAvatarModalClose"
      />
      
      <el-main>
        <!-- 左侧触发按钮 - 在帖子广场、我的收藏、我的帖子、个人中心、私信聊天页面显示 -->
        <div class="sidebar-trigger-left fixed-trigger" @click="toggleSubmenu">
          <el-icon v-if="!showSubmenu"><ArrowRightBold /></el-icon>
          <el-icon v-else><Close /></el-icon>
        </div>
        
        <!-- 首页内容显示区域 -->
        <div v-if="activeTab === 'dashboard'" class="tab-content">
          <DashboardView />
        </div>
        
        <div v-if="activeTab === 'square'" class="tab-content">
          <PostListView @show-post-detail="showPostDetail" @need-login="goToLogin" />
        </div>
        
        <!-- 用户收藏内容显示区域 -->
        <div v-if="activeTab === 'favorites'" class="tab-content">
          <UserFavoritesView @show-post-detail="showPostDetail" @need-login="goToLogin" />
        </div>
        
        <!-- AI问答内容显示区域 -->
        <div v-if="activeTab === 'ai'" class="tab-content">
          <AiView />
        </div>
        
        <!-- 个人中心内容显示区域 -->
        <div v-if="activeTab === 'profile'" class="tab-content">
          <ProfileView v-if="computedIsLoggedIn" />
          <div v-else class="not-logged-in">
            <h3>🌸 欢迎来到钩织乐园</h3>
            <p>登录后即可记录您的钩织历程，与其他爱好者交流心得</p>
            <el-button type="primary" @click="goToLogin" round>立即登录</el-button>
          </div>
        </div>
        
        <!-- 用户详情模态框 -->
        <OtherUserProfileModal
          v-model="showOtherUserProfileModal"
          :userId="targetUserId!"
          @close="handleOtherUserProfileClose"
        />
        
        <!-- 用户帖子内容显示区域 -->
        <div v-if="activeTab === 'userPosts'" class="tab-content">
          <UserPostsView @show-post-detail="showPostDetail" @need-login="goToLogin" />
        </div>
        
        <!-- 好友列表内容显示区域 - 已合并到聊天页面中 -->
        <!-- 聊天内容显示区域 -->
        <div v-if="activeTab === 'chat'" class="tab-content">
          <ChatView />
        </div>
        
        <!-- 系统公告内容显示区域 -->
        <div v-if="activeTab === 'announcements'" class="tab-content">
          <AnnouncementCenterView />
        </div>
        
        <!-- 互动消息内容显示区域 -->
        <div v-if="activeTab === 'notifications'" class="tab-content">
          <NotificationCenterView />
        </div>
        
        <!-- 帖子详情页面，当路由参数中有postId时显示 -->
        <div v-if="activeTab === 'postDetail'" class="tab-content">
          <PostDetailView :postId="postIdFromRoute!" />
        </div>
        
        <!-- 消息页面，当路由匹配消息路径时显示 - 已合并到聊天页面中 -->
      </el-main>
    </el-container>
    
    <!-- 创建帖子弹窗 -->
    <el-dialog
      v-model="showCreatePostDialog"
      title="发布帖子"
      width="60%"
      :before-close="handleCloseCreatePostDialog"
      :close-on-click-modal="false"
      destroy-on-close
      class="create-post-dialog"
    >
      <PostCreateView @post-created="onPostCreated" @cancel="handleCloseCreatePostDialog" />
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { ArrowDown, SwitchButton, Star, EditPen, Document, Close, ArrowRightBold, House, User, ChatLineRound, SetUp, ArrowLeft, ChatDotSquare, Bell, BellFilled, Delete, ChatDotRound, MagicStick } from '@element-plus/icons-vue';
import PostListView from '@/user/views/post/PostListView.vue';
import PostDetailView from '@/user/views/post/PostDetailView.vue';
import UserFavoritesView from '@/user/views/post/UserFavoritesView.vue';
import UserPostsView from '@/user/views/post/UserPostsView.vue';
import PostCreateView from '@/user/views/post/PostCreateView.vue'; // 导入创建帖子组件
import AvatarUploadModal from '@/user/views/user/AvatarUploadModal.vue'; // 导入头像上传弹窗组件
import ProfileView from '@/user/views/user/ProfileView.vue'; // 导入个人资料组件
import OtherUserProfileModal from '@/user/views/user/OtherUserProfileModal.vue'; // 导入其他用户资料模态框组件
import ChatView from '@/user/views/message/ChatView.vue'; // 导入聊天组件
import AiView from '@/user/views/aichat/AiView.vue'; // 导入AI问答组件
import AnnouncementCenterView from '@/user/views/message/AnnouncementCenterView.vue'; // 导入公告中心组件
import NotificationCenterView from '@/user/views/message/NotificationCenterView.vue'; // 导入通知中心组件
import DashboardView from '@/user/views/dashboard/DashboardView.vue'; // 导入首页组件
import { startPollingUnreadCounts, stopPollingUnreadCounts, unreadAnnouncements, unreadNotifications, initWebSocketNotifications } from '@/user/services/notificationService';
import { notificationApi } from '@/user/api/notification';
import { userAnnouncementApi } from '@/user/api/announcement';
import { getUnreadMessageCount } from '@/user/api/interaction'; // 导入获取未读消息数的API

const router = useRouter();
const route = useRoute();
const store = useStore();

const activeTab = ref('dashboard'); // 默认显示首页
const questionInput = ref(''); 
const postIdFromRoute = ref<number | null>(null);
const showSubmenu = ref(false);
const showCreatePostDialog = ref(false); // 控制创建帖子弹窗显示
const showAvatarUploadModal = ref(false); // 控制头像上传弹窗显示
const showOtherUserProfileModal = ref(false); // 控制其他用户资料模态框显示
const targetUserId = ref<number | null>(null); // 目标用户ID，用于消息页面
const showMessageMenu = ref(false); // 控制消息下拉菜单显示
const unreadChatMessages = ref(0); // 新增：存储未读私信数

// 计算未读总数
const totalUnread = computed(() => {
  return unreadAnnouncements.value + unreadNotifications.value + unreadChatMessages.value;
});

// 使用计算属性来响应store的变化
const computedIsLoggedIn = computed(() => store.getters.isAuthenticated);
const computedUserInfo = computed(() => store.state.userInfo);

// 监听登录状态变化，开始或停止轮询
watch(computedIsLoggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
    // 初始化WebSocket通知处理器，确保能接收公告和通知消息
    initWebSocketNotifications();
    startPollingUnreadCounts(30000); // 每30秒轮询一次
  } else {
    stopPollingUnreadCounts();
    // 登出时重置未读数
    unreadAnnouncements.value = 0;
    unreadNotifications.value = 0;
  }
}, { immediate: true });

// 控制二级菜单的显示与隐藏
const toggleSubmenu = (e: Event) => {
  e.stopPropagation(); // 阻止事件冒泡
  showSubmenu.value = !showSubmenu.value;
  
  // 如果展开菜单且用户已登录，则获取未读私信数
  if (showSubmenu.value && computedIsLoggedIn.value) {
    fetchUnreadChatCount();
  }
};

// 专门用于帖子广场的点击事件
const goToSquare = () => {
  selectTab('square');
  // 如果菜单是隐藏的，点击帖子广场时显示菜单
  if (!showSubmenu.value) {
    showSubmenu.value = true;
    // 如果用户已登录，则获取未读私信数
    if (computedIsLoggedIn.value) {
      fetchUnreadChatCount();
    }
  }
};

// 点击页面其他地方时隐藏菜单
const handleClickOutside = (e: Event) => {
  const target = e.target as HTMLElement;
  const navItem = target.closest('.nav-item');
  const submenu = target.closest('.sidebar-submenu');
  const messageDropdown = target.closest('.message-dropdown');
  
  // 如果点击的不是导航项也不是二级菜单，也不是消息下拉菜单，则隐藏所有菜单
  if (!navItem && !submenu && !messageDropdown) {
    showSubmenu.value = false;
    showMessageMenu.value = false;
  }
};

// 选择标签页
const selectTab = (tab: string) => {
  activeTab.value = tab;
  
  // 根据标签页更新路由，但不包括帖子详情页，因为它由路由参数控制
  if(tab !== 'postDetail') {
    let path = '/';
    if(tab === 'dashboard') path = '/';  // 首页对应根路径
    else if(tab === 'square') path = '/post';  // 帖子广场对应 /post 路由
    else if(tab === 'ai') path = '/ai';
    else if(tab === 'profile') path = '/profile';
    else if(tab === 'favorites') path = '/user/favorites';
    else if(tab === 'userPosts') path = '/user/posts';
    else if(tab === 'friends') path = '/user/friends'; // 添加好友路由
    else if(tab === 'chat') path = '/user/chat'; // 添加聊天路由
    else if(tab === 'announcements') path = '/announcements'; // 添加公告路由
    else if(tab === 'notifications') path = '/notifications'; // 添加通知路由
    else if(tab === 'message') path = `/user/message/${targetUserId.value}`; // 消息路由
    
    if(route.path !== path) {
      router.push(path);
    }
  }
  
  // 切换标签页时隐藏二级菜单
  showSubmenu.value = false;
  showMessageMenu.value = false;
};

// 以下是导航到二级菜单项的方法（仅占位，不实现具体逻辑）
const goToUserCollection = () => {
  // 切换到用户收藏内容显示
  activeTab.value = 'favorites';
  showSubmenu.value = false;
};

const goToCreatePost = () => {
  // 显示创建帖子弹窗
  console.log("显示创建帖子弹窗");
  showCreatePostDialog.value = true;
  showSubmenu.value = false;
};

const goToPersonalPosts = () => {
  // 切换到个人帖子内容显示
  activeTab.value = 'userPosts';
  showSubmenu.value = false;
};

// 添加公告中心导航方法
const goToAnnouncements = () => {
  activeTab.value = 'announcements';
  showSubmenu.value = false;
  showMessageMenu.value = false;
};

// 添加通知中心导航方法
const goToNotifications = () => {
  activeTab.value = 'notifications';
  showSubmenu.value = false;
  showMessageMenu.value = false;
};

// 添加聊天功能相关方法
const goToChat = async () => {
  // 在跳转之前获取最新的未读消息数
  await fetchUnreadChatCount();
  activeTab.value = 'chat';
  showSubmenu.value = false;
};

// 返回帖子广场
const goBackToSquare = () => {
  activeTab.value = 'square';
  postIdFromRoute.value = null; // 清除帖子ID
  router.push('/post'); // 跳转到帖子列表页，实际上还是在 UserHomeView 中
};

// 跳转到管理后台登录页
const goToAdminLogin = () => {
  router.push({ name: 'admin-login' });
};

// 跳转到登录页
const goToLogin = () => {
  router.push({ name: 'login' });
};

// 处理消息下拉菜单命令
const handleMessageCommand = (command: string) => {
  if(command === 'announcements') {
    activeTab.value = 'announcements';
    router.push('/announcements');
  } else if(command === 'notifications') {
    activeTab.value = 'notifications';
    router.push('/notifications');
  }
  showMessageMenu.value = false;
};

// 处理下拉菜单命令
const handleCommand = async (command: string) => {
  if (command === 'logout') {
    try {
      await ElMessageBox.confirm(
        '确定要退出登录吗？',
        '确认退出',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      );

      // 调用store中的logout action
      const logoutSuccess = await store.dispatch('logout');
      
      if (logoutSuccess) {
        ElMessage.success('已退出登录');
        // 跳转到登录页面
        router.push({ name: 'login' });
      } else {
        ElMessage.error('退出登录失败');
      }
    } catch (error) {
      // 用户取消了操作
      console.log('用户取消退出登录');
    }
  } else if (command === 'announcements') {
    // 跳转到系统公告页面
    router.push({ name: 'announcements' });
    // 关闭下拉菜单
    activeTab.value = 'announcements';
  } else if (command === 'notifications') {
    // 跳转到互动消息页面
    router.push({ name: 'notifications' });
    // 关闭下拉菜单
    activeTab.value = 'notifications';
  } else if (command === 'markAllAsRead') {
    try {
      // 标记所有公告为已读
      if (unreadAnnouncements.value > 0) {
        // 获取所有未读公告并批量标记
        const response = await userAnnouncementApi.getAnnouncementList(1, 9999);
        if (response.data && response.data.records) {
          const unreadAnnouncementsList = response.data.records.filter(ann => ann.isRead === 0);
          const unreadIds = unreadAnnouncementsList.map(ann => ann.id);
          if (unreadIds.length > 0) {
            await userAnnouncementApi.batchMarkAsRead(unreadIds);
          }
        }
      }
      // 标记所有通知为已读
      if (unreadNotifications.value > 0) {
        await notificationApi.markAllNotificationsAsRead();
      }
      ElMessage.success('已标记全部为已读');
      // 刷新未读消息计数
      startPollingUnreadCounts(30000);
    } catch (error) {
      console.error('标记全部已读失败:', error);
      ElMessage.error('标记全部已读失败');
    }
  } else if (command === 'clearAllNotifications') {
    try {
      await ElMessageBox.confirm(
        '此操作将永久删除所有消息，是否继续？',
        '警告',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      );

      // 清除所有通知
      await notificationApi.clearAllNotifications();
      ElMessage.success('已清除全部消息');
      // 刷新未读消息计数
      startPollingUnreadCounts(30000);
    } catch (error) {
      if (error !== 'cancel') {
        console.error('清除消息失败:', error);
        ElMessage.error('清除消息失败');
      }
    }
  }
};

// 添加处理显示帖子详情的方法
const showPostDetail = (postId: number) => {
  postIdFromRoute.value = postId;
  activeTab.value = 'postDetail';
  // 现在启用路由跳转，以便URL反映当前状态
  router.push(`/post/${postId}`);
};

// 监听路由变化，统一处理所有路径情况
watch(
  () => route.path,
  (newPath) => {
    if(newPath === '/' || newPath === '/post') {
      if(newPath === '/') {
        activeTab.value = 'dashboard';
      } else {
        activeTab.value = 'square';
      }
      // 如果路由切换到了其他页面，隐藏二级菜单
      showSubmenu.value = false;
    } else if(newPath === '/ai') {
      activeTab.value = 'ai';
      showSubmenu.value = false;
    } else if(newPath === '/profile') { // 添加个人资料路由处理
      activeTab.value = 'profile';
      showSubmenu.value = false;
    } else if(newPath.startsWith('/profile/')) { // 处理 /profile/:userId 路由，显示其他用户资料
      const pathParts = newPath.split('/');
      const userId = parseInt(pathParts[pathParts.length - 1]);
      if(!isNaN(userId)) {
        targetUserId.value = userId;
        showOtherUserProfileModal.value = true;
        showSubmenu.value = false;
      }
    } else if(newPath === '/user/chat') {
      activeTab.value = 'chat';
      showSubmenu.value = false;
    } else if(newPath === '/announcements') { // 添加公告路由处理
      activeTab.value = 'announcements';
      showSubmenu.value = false;
    } else if(newPath === '/notifications') { // 添加通知路由处理
      activeTab.value = 'notifications';
      showSubmenu.value = false;
    } else if(newPath.startsWith('/user/message/')) { // 添加消息路由处理
      // 提取用户ID
      const pathParts = newPath.split('/user/message/');
      const userId = parseInt(pathParts[1]);
      if(!isNaN(userId)) {
        targetUserId.value = userId;
        activeTab.value = 'message';
        showSubmenu.value = false;
      }
    } else if(newPath.match(/^\/post\/\d+$/)) {
      // 匹配 /post/:id 路径，提取ID并显示详情页
      const postId = parseInt(newPath.split('/')[2]);
      if(!isNaN(postId)) {
        postIdFromRoute.value = postId;
        activeTab.value = 'postDetail';
        showSubmenu.value = false;
      }
    }
  },
  { immediate: true }
);

// 监听路由参数变化，当postId参数变化时更新
watch(
  () => route.params,
  (newParams) => {
    // 检查是否是消息路由参数变化
    if(newParams.userId) {
      const userId = parseInt(newParams.userId as string);
      if(!isNaN(userId)) {
        targetUserId.value = userId;
        activeTab.value = 'message';
        showSubmenu.value = false;
      }
    } else if(newParams.postId) {
      const postId = Number(newParams.postId);
      if(postId) {
        postIdFromRoute.value = postId;
        activeTab.value = 'postDetail';
        showSubmenu.value = false;
      } else if(activeTab.value === 'postDetail') {
        // 如果URL中不再有postId参数，返回广场
        activeTab.value = 'square';
      }
    }
  },
  { immediate: true }
);

// 处理关闭创建帖子弹窗
const handleCloseCreatePostDialog = () => {
  showCreatePostDialog.value = false;
};

// 处理帖子创建成功事件
const onPostCreated = () => {
  showCreatePostDialog.value = false;
  ElMessage.success('帖子发布成功！');
  // 可以刷新当前页面内容，或者跳转到相应页面
  if (activeTab.value !== 'square') {
    selectTab('square');
  }
  // 刷新帖子列表
  // 这里可以触发列表刷新逻辑
};

// 打开头像上传弹窗
const openAvatarUploadModal = () => {
  showAvatarUploadModal.value = true;
};

// 生成带时间戳的头像URL以避免缓存问题
const getAvatarSrc = (avatarUrl: string | undefined) => {
  if (!avatarUrl) {
    // 使用默认头像
    return 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png';
  }
  
  // 检查是否是完整的URL（包含协议）
  let fullUrl = '';
  if (avatarUrl.startsWith('http')) {
    fullUrl = avatarUrl;
  } else if (avatarUrl.startsWith('/')) {
    // 如果是绝对路径，拼接基础URL
    fullUrl = `${window.location.origin}${avatarUrl}`;
  } else {
    // 如果是相对路径，拼接基础URL
    fullUrl = `${window.location.origin}/${avatarUrl}`;
  }
  
  // 添加时间戳参数以避免缓存
  const url = new URL(fullUrl);
  url.searchParams.set('t', Date.now().toString());
  return url.toString();
};

// 处理头像上传成功事件
const handleAvatarUploaded = () => {
  // 头像已在AvatarUploadModal中更新到store，此处无需额外操作
  // computedUserInfo会自动响应store中的变化
  ElMessage.success('头像更新成功！');
  showAvatarUploadModal.value = false;
};

// 处理头像上传弹窗关闭事件
const handleAvatarModalClose = () => {
  showAvatarUploadModal.value = false;
};

// 处理其他用户资料模态框关闭事件
const handleOtherUserProfileClose = () => {
  showOtherUserProfileModal.value = false;
  // 不再跳转到首页，保持在当前页面
};

// 处理头像点击事件
const handleAvatarClick = () => {
  if (computedIsLoggedIn.value) {
    openAvatarUploadModal();
  } else {
    // 如果未登录，跳转到登录页面
    goToLogin();
  }
};

// 添加获取未读私信数的方法
const fetchUnreadChatCount = async () => {
  try {
    const response = await getUnreadMessageCount();
    if (response.code === 1) {
      unreadChatMessages.value = response.data || 0;
    } else {
      console.error('获取未读消息数失败:', response.msg);
      unreadChatMessages.value = 0;
    }
  } catch (error) {
    console.error('获取未读消息数异常:', error);
    unreadChatMessages.value = 0;
  }
};

onMounted(() => {
  // 初始化WebSocket通知处理器，确保能接收公告和通知消息
  if (computedIsLoggedIn.value) {
    initWebSocketNotifications();
  }
  document.addEventListener('click', handleClickOutside);
});

// 组件销毁前移除事件监听器
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  // 组件卸载时停止轮询
  stopPollingUnreadCounts();
});
</script>

<style scoped>
/* 侧边栏样式的二级菜单，从上方滑入 */
.sidebar-submenu {
  position: fixed;
  top: 120px; /* 位于触发按钮下方，增加一些间距 */
  left: 20px; /* 与触发按钮对齐 */
  width: 250px;
  background: white;
  border-radius: 8px; /* 调整为全圆角 */
  box-shadow: 2px 6px 16px rgba(0, 0, 0, 0.15);
  z-index: 998; /* 确保在触发按钮下方 */
  height: auto;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: linear-gradient(135deg, #fffdf9 0%, #fdfaf3 100%);
  border: 1px solid #e9dcc8;
}

.submenu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 15px 10px 20px;
  border-bottom: 1px solid #eee;
  margin-bottom: 5px;
  background: linear-gradient(to right, #f9f2eb, #fefefe);
  border-radius: 8px 8px 0 0; /* 顶部圆角 */
}

.submenu-title {
  font-weight: bold;
  color: #8B7D6B;
  font-size: 16px;
}

.close-btn {
  padding: 5px;
  color: #d48341;
  font-size: 18px;
}

.close-btn:hover {
  background-color: #f9f2eb;
  color: #c06d2c;
}

.submenu-list {
  list-style: none;
  padding: 10px 0 0;
  margin: 0;
  width: 100%;
  flex-grow: 1;
}

.submenu-item {
  display: flex;
  align-items: center;
  justify-content: flex-start; /* 左对齐 */
  padding: 12px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #3c3c3c;
  border-left: 3px solid transparent; /* 左侧边框 */
  min-height: 46px; /* 最小高度 */
  box-sizing: border-box;
  position: relative;
}

.submenu-item:hover {
  background-color: #f9f2eb;
  color: #d48341;
  border-left: 3px solid #d48341;
  padding-left: 23px;
}

.submenu-icon {
  margin-right: 10px; /* 图标与文字之间的间距 */
  font-size: 18px;
  width: 24px;
  text-align: center;
  flex-shrink: 0; /* 图标不收缩 */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d48341;
  position: relative;
}

.badge-overlay {
  position: absolute;
  top: -5px;
  right: 0;
  transform: translateX(50%);
}

/* 为菜单项中的文本添加样式 */
.submenu-item span {
  flex: 0 0 auto; /* 文字不伸缩，根据内容自适应 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 15px;
  letter-spacing: 0.5px;
  line-height: 1.4;
  display: inline-block;
}

/* 导入手写风格字体 */
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@500;600&display=swap');

.common-layout {
  min-height: 100vh;
  background: linear-gradient(135deg, #fefefe 0%, #f8f3eb 100%);
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
}

/* 背景编织纹理 */
.common-layout::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(#d4c19c 1px, transparent 1px),
    radial-gradient(#d4c19c 1px, transparent 1px);
  background-size: 40px 40px;
  background-position: 0 0, 20px 20px;
  opacity: 0.1;
  z-index: -1;
  pointer-events: none;
}

/* 固定定位的侧边栏触发按钮 */
.fixed-trigger {
  position: fixed;
  top: 80px; /* 距离顶部的距离，避开导航栏 */
  left: 20px; /* 距离左边的距离 */
  z-index: 1001; /* 确保层级高于其他内容 */
  background: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9dcc8;
  color: #d48341;
  transition: all 0.3s ease;
}

.fixed-trigger:hover {
  background-color: #f9f2eb;
  transform: scale(1.1);
}

.header-nav {
  padding: 0;
  background-color: #f9f2eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: auto;
  min-height: 60px;
  display: flex;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid #e9dcc8;
}

.nav-items {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  justify-content: space-between;
  padding: 0 10px;
  position: relative; /* 为二级菜单定位做准备 */
}

.logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #d48341;
  font-weight: bold;
  font-size: 22px;
  padding: 10px 20px;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
}

.logo::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 20px;
  width: calc(100% - 40px);
  height: 2px;
  background: linear-gradient(to right, #d48341, transparent);
  transform-origin: left;
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.logo:hover::after {
  transform: scaleX(0.7);
}

.logo .crochet-icon {
  font-size: 24px;
  margin-right: 8px;
  color: #d48341;
}

.logo .logo-text {
  margin-left: 8px;
  font-family: 'Quicksand', cursive;
  color: #8B7D6B;
}

.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: 12px 20px;
  margin: 0 4px;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #8B7D6B;
  font-weight: 500;
  font-size: 15px;
  white-space: nowrap;
  flex-shrink: 0;
}

.nav-item:hover {
  background: linear-gradient(135deg, #e8d8c4 0%, #f0e4d0 100%);
  color: #d48341;
}

.nav-item.active {
  background: linear-gradient(135deg, #d48341 0%, #ca7836 100%);
  color: white;
  box-shadow: 0 4px 8px rgba(212, 131, 65, 0.3);
}

.nav-icon {
  margin-right: 8px;
  font-size: 18px;
}

.message-dropdown {
  position: absolute;
  top: 60px;
  right: 20px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 10px 0;
}

.message-dropdown-button {
  border: none;
  background: transparent;
  padding: 0;
}

/* 从左侧滑入动画 */
.slide-from-left-enter-active {
  transition: all 0.3s ease;
}
.slide-from-left-leave-active {
  transition: all 0.2s ease;
}
.slide-from-left-enter-from {
  transform: translateX(-100%); /* 从左侧外部进入 */
  opacity: 0;
}
.slide-from-left-leave-to {
  transform: translateX(-100%); /* 离开时也向左侧消失 */
  opacity: 0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-shrink: 0;
  margin-left: auto;
}

.user-avatar {
  cursor: pointer;
  border: 2px solid #f0e4d0;
  margin-right: 0;
}

.username {
  color: #8B7D6B;
  cursor: pointer;
  font-weight: 500;
}

.dropdown-link {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: #5c5142;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 16px;
  transition: all 0.3s;
  white-space: nowrap;
}

.dropdown-link:hover {
  background-color: #e8d8c4;
  color: #d48341;
}

/* 响应式设计：在小屏幕上隐藏部分导航项 */
@media screen and (max-width: 1200px) {
  .nav-item:not(.active):not(:first-child):not(:nth-child(2)):not(:nth-child(3)) {
    display: none;
  }
}

@media screen and (max-width: 992px) {
  .nav-item:nth-last-child(1),
  .nav-item:nth-last-child(2) {
    display: none;
  }
  
  .nav-item.active {
    order: -1;
  }
}

@media screen and (max-width: 768px) {
  .nav-items {
    flex-wrap: wrap;
  }
  
  .nav-item {
    padding: 8px 15px;
    margin: 5px;
    font-size: 14px;
  }
  
  .nav-item:not(.active):not(:first-child) {
    display: none;
  }
  
  .user-info {
    margin-left: auto;
  }
  
  .dropdown-link {
    font-size: 14px;
  }
}

@media screen and (max-width: 576px) {
  .logo .logo-text {
    display: none;
  }
  
  .nav-item span {
    display: none;
  }
  
  .nav-item {
    padding: 10px;
    border-radius: 50%;
  }
  
  .nav-icon {
    margin-right: 0;
    font-size: 20px;
  }
  
  .dropdown-link span {
    display: none;
  }
  
  .dropdown-link i {
    margin-right: 0;
  }
}

.tab-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 10px;
}

.section-header {
  text-align: center;
  margin-bottom: 30px;
}

.section-header h2 {
  font-size: 28px;
  color: #8B7D6B;
  margin-bottom: 8px;
}

.section-header p {
  color: #a09585;
  font-size: 16px;
}

.profile-content {
  padding: 20px;
}

.profile-card {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.profile-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 25px;
}

.profile-large-avatar {
  margin-bottom: 15px;
}

.profile-basic-info h3 {
  margin: 5px 0 10px;
  color: #3c3c3c;
  font-size: 22px;
}

.profile-basic-info p {
  color: #a09585;
  font-size: 14px;
}

.profile-stats {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  gap: 40px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: #d48341;
}

.stat-label {
  font-size: 14px;
  color: #a09585;
}

.profile-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
}

.not-logged-in {
  text-align: center;
  padding: 40px 20px;
}

.not-logged-in h3 {
  color: #8B7D6B;
  margin-bottom: 15px;
}

.username.login-prompt {
  cursor: pointer;
  color: #8B7D6B;
  font-weight: 500;
  transition: all 0.3s;
  padding: 5px 10px;
  border-radius: 16px;
}

.username.login-prompt:hover {
  background-color: #e8d8c4;
  color: #d48341;
}

.user-avatar.no-click {
  cursor: not-allowed;
  opacity: 0.8;
}

.user-avatar.no-click:hover {
  transform: none;
  box-shadow: none;
}

.message-count-badge {
  margin-left: 8px;
  transform: scale(0.8);
}

.message-count-badge ::v-deep(.el-badge__content) {
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 12px;
  height: 18px;
  min-width: 18px;
  line-height: 14px;
}
</style>